/**
 * @file hasPerm — helper canónico de gating cliente-side por permiso
 * atómico con fallback a role-list (feature 063 — access control
 * hardening).
 *
 * Reemplaza el patrón inline `session?.rol === "superuser"` regado en
 * widgets/rutas/sidebar. El SSOT del backend siempre gana — este
 * helper es UX (qué se ve en sidebar/dashboard) — el backend valida
 * cada request con RBAC.Authorize independientemente.
 *
 * Patrón:
 *   - Si `session.permissions[]` viene poblado (feature 031+), gate
 *     por inclusión del slug atómico.
 *   - Si viene vacío (sesión legacy, backend antes de 031, o cache
 *     stale post-edit del rol), fallback a la role-list provista.
 *   - Sin sesión → false siempre.
 */
import type { SessionUser } from "../../features/login/lib/auth";

export function hasPerm(
  session: SessionUser | null,
  perm: string,
  fallbackRoles: ReadonlyArray<string> = ["superuser"],
): boolean {
  if (!session) return false;
  if (session.permissions && session.permissions.length > 0) {
    return session.permissions.includes(perm);
  }
  return fallbackRoles.includes(session.rol);
}

/**
 * hasAnyPerm — true si la sesión tiene AL MENOS uno de los permisos.
 * Útil para módulos con varias entradas (ej. b2b.read OR b2b.write).
 */
export function hasAnyPerm(
  session: SessionUser | null,
  perms: ReadonlyArray<string>,
  fallbackRoles: ReadonlyArray<string> = ["superuser"],
): boolean {
  if (!session) return false;
  if (session.permissions && session.permissions.length > 0) {
    return perms.some((p) => session.permissions!.includes(p));
  }
  return fallbackRoles.includes(session.rol);
}
